<script setup>
import { computed } from 'vue'
import { trackSelectItem } from '@/frontend/vue/composables/useGTMTracking'

const props = defineProps({
    product: Object,
    isBrochureMode: {
        type: [Boolean, String],
        default: false
    },
    showPrices: {
        type: [Boolean, String],
        default: true
    },
    showBotanical: {
        type: [Boolean, String],
        default: false
    },
    showCommonNameSubtitle: {
        type: [Boolean, String],
        default: false
    },
    listName: {
        type: String,
        default: 'Product List'
    },
    index: {
        type: Number,
        default: 0
    },
    defaultImage: {
        type: String,
        default: '/images/default.jpg'
    }
})

// Convert string props to boolean
const isBrochureMode = computed(() => {
    return props.isBrochureMode === true || props.isBrochureMode === 'true'
})

const showPrices = computed(() => {
    return props.showPrices === true || props.showPrices === 'true'
})

const shouldShowBotanicalName = computed(() => {
    const isEnabled = props.showBotanical === 'true' || props.showBotanical === true
    const displayName = props.product?.display_html || props.product?.html
    const botanicalName = props.product?.html
    const namesDiffer = displayName !== botanicalName
    return isEnabled && namesDiffer && botanicalName
})

const shouldShowCommonNameSubtitle = computed(() => {
    const isEnabled = props.showCommonNameSubtitle === 'true' || props.showCommonNameSubtitle === true
    return isEnabled
        && props.product?.common_name
        && props.product.common_name !== props.product?.display_name
})

const badges = computed(() => {
    if (!Array.isArray(props.product?.badges)) {
        return []
    }

    return props.product.badges
        .filter((badge) => badge && badge.label && badge.identifier)
        .map((badge) => {
            const identifier = String(badge.identifier)
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9-]+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '')

            return {
                label: String(badge.label).trim(),
                identifier,
            }
        })
        .filter((badge) => badge.label !== '' && badge.identifier !== '')
})

function handleProductClick() {
    trackSelectItem(props.product, props.listName, props.index)
}

</script>

<template>
    <a :href="`/products/${product.handle}`" @click="handleProductClick">
        <figure class="product-card__image-area">
            <img :src="product.listing_image || product.image || defaultImage" alt="">
        </figure>
        <div v-if="badges.length" class="product-card__badges">
            <span
                v-for="badge in badges"
                :key="badge.identifier"
                class="pill"
                :class="`pill-${badge.identifier}`"
            >
                {{ badge.label }}
            </span>
        </div>
        <div class="product-card__content">
            <h3 class="product-card__title" v-html="product.display_html || product.html"></h3>
            <div class="product-card__botanical-name" v-if="shouldShowCommonNameSubtitle">
                {{ product.common_name }}
            </div>
            <div class="product-card__botanical-name" v-if="shouldShowBotanicalName" v-html="product.html"></div>
            <!-- Show pricing only when NOT in brochure mode -->
            <h4 class="product-card__subtitle" v-if="!isBrochureMode && product.is_in_stock && product.from_price">
                From ${{ product.from_price.toFixed(2) }}
            </h4>
            <!-- Show out of stock message only when NOT in brochure mode -->
            <h4 class="product-card__subtitle product-card__subtitle--out-of-stock" v-else-if="!isBrochureMode && !product.is_in_stock">
                {{ product.out_of_stock_message || 'Out of Stock' }}
            </h4>
            <!-- In brochure mode, hide all pricing/stock info -->
        </div>
    </a>
</template>
